'use client'

import { useStatusToast, UseStatusToastOptions } from './useStatusToast'
import { ACTION_FAILED_EVENT, ACTION_SUCCESS_EVENT, SUBMIT_SUCCESS_EVENT } from '../../../../MyStichtingOpenHelpers/src/static'
import { useEffect, useRef } from 'react'

export type UseFormToastListenerOptions = UseStatusToastOptions & {
    formId?: string
    preventToasts?: boolean
    /** When set, return a toast message for this action-success event; otherwise the default SUCCESS key is used. */
    resolveActionSuccessMessage?: (event: Event) => string | undefined
    /** When set, return a toast message for this action-failed event; otherwise the default FAILURE key is used. */
    resolveActionFailedMessage?: (event: Event) => string | undefined
}

const eventDetailMatchesForm = (event: Event, formId: string | undefined): boolean => {
    if (formId === undefined) return true
    const detail = (event as CustomEvent<{ formId?: string }>).detail
    return detail?.formId === formId
}

/**
 * Subscribes to document-level form and action lifecycle events (submit success, action success,
 * action failure) and updates a shared status toast: submit success opens the loading state;
 * action outcomes call success or error toasts. Uses {@link useStatusToast} for messages resolved
 * from `translationKeys` via the dictionary context.
 *
 * When `formId` is set, events without a matching `detail.formId` are ignored so several forms
 * can share a page without interfering with each other’s toasts.
 *
 * @param options - Form scope and translation keys for the status toast.
 * @param options.formId - Optional id; only events whose `detail.formId` equals this value are handled.
 * @param options.translationKeys - Keys for pending, success, and failure copy shown in the toast.
 * @param options.preventToasts - When true, the toasts are blocked and not shown.
 * @param options.resolveActionSuccessMessage - Optional resolver for the success toast copy.
 * @param options.resolveActionFailedMessage - Optional resolver for the failure toast copy.
 * @returns `{ toast }` with `open`, `succeed`, `fail`, and `hide` from `useStatusToast`.
 */
export const useFormToastListener = ({
    formId,
    translationKeys,
    preventToasts = false,
    resolveActionSuccessMessage,
    resolveActionFailedMessage,
}: UseFormToastListenerOptions) => {
    const toast = useStatusToast({ translationKeys })
    const toastRef = useRef(toast)
    toastRef.current = toast

    useEffect(() => {
        if (preventToasts) return
        const controller = new AbortController()
        const { signal } = controller

        const onSubmitSuccess = (event: Event): void => {
            if (!eventDetailMatchesForm(event, formId)) return
            toastRef.current.open()
        }
        const onActionFailed = (event: Event): void => {
            if (!eventDetailMatchesForm(event, formId)) return
            toastRef.current.fail(resolveActionFailedMessage?.(event))
        }
        const onActionSuccess = (event: Event): void => {
            if (!eventDetailMatchesForm(event, formId)) return
            toastRef.current.succeed(resolveActionSuccessMessage?.(event))
        }

        document.addEventListener(SUBMIT_SUCCESS_EVENT, onSubmitSuccess, { signal })
        document.addEventListener(ACTION_FAILED_EVENT, onActionFailed, { signal })
        document.addEventListener(ACTION_SUCCESS_EVENT, onActionSuccess, { signal })

        return () => {
            controller.abort()
        }
    }, [formId, preventToasts, resolveActionSuccessMessage, resolveActionFailedMessage])

    return {
        toast,
    }
}
